import type { WorkbenchMode } from '@/capabilities/service-operations/api/workbench';
import { WORKBENCH_PATHS, type WorkbenchView } from './workbenchPaths';

/**
 * The three Workbench roles (signed product decision, 2026-10-05): Engineer,
 * Dispatcher, Manager. This is the one place that maps a concrete user role
 * (hyperion-be internal/core/user.Role, users.role) onto the role the UI keys
 * off — landing, chair modes and operational views. It does not grant
 * authority: every action is still enforced by the backend guard
 * (pkg/middleware/roles.go, mirrored by roleGuards.ts).
 *
 * L1/L2 engineers are mostly on the phone, so the Engineer landing is the
 * caller lookup (caller -> contact, company, devices, tickets, one-click
 * remote). L3 engineers land on Engineer Day.
 */
export type WorkbenchRole = 'engineer' | 'dispatcher' | 'manager';

export const WORKBENCH_ROLE_LABELS: Readonly<Record<WorkbenchRole, string>> = {
  engineer: 'Engineer',
  dispatcher: 'Dispatcher',
  manager: 'Manager',
};

interface Membership {
  role: WorkbenchRole;
  /** Chair modes in switcher order; the first is the default. */
  modes: readonly WorkbenchMode[];
  landing: string;
  /** Holds backend `RequireRole("engineer")` authority (operational staff). */
  operational: boolean;
  /** Holds backend `RequireRole("admin")` authority. */
  admin: boolean;
}

const ALL_MODES: readonly WorkbenchMode[] = [
  'operations',
  'my-work',
  'portfolio',
  'schedule',
  'scorecard',
];

function engineer(landing: string): Membership {
  return { role: 'engineer', modes: ['my-work'], landing, operational: true, admin: false };
}

const MEMBERSHIPS: ReadonlyMap<string, Membership> = new Map<string, Membership>([
  ['engineer', engineer(WORKBENCH_PATHS.caller)],
  ['engineer_l0', engineer(WORKBENCH_PATHS.caller)],
  ['engineer_l1', engineer(WORKBENCH_PATHS.caller)],
  ['engineer_l2', engineer(WORKBENCH_PATHS.caller)],
  ['engineer_l3', engineer(WORKBENCH_PATHS.engineerDay)],
  // Legacy "user" alias: lands like an engineer but has no engineer authority.
  [
    'user',
    {
      role: 'engineer',
      modes: ['operations'],
      landing: WORKBENCH_PATHS.root,
      operational: false,
      admin: false,
    },
  ],
  [
    'dispatcher',
    {
      role: 'dispatcher',
      modes: ['schedule', 'operations', 'my-work'],
      landing: WORKBENCH_PATHS.dispatch,
      operational: true,
      admin: false,
    },
  ],
  [
    'sdm',
    {
      role: 'dispatcher',
      modes: ['operations', 'schedule', 'scorecard'],
      landing: WORKBENCH_PATHS.dispatch,
      operational: true,
      admin: false,
    },
  ],
  [
    'manager',
    {
      role: 'manager',
      modes: ['operations', 'schedule', 'scorecard'],
      landing: WORKBENCH_PATHS.operationsChair,
      operational: true,
      admin: false,
    },
  ],
  [
    'admin',
    {
      role: 'manager',
      modes: ALL_MODES,
      landing: WORKBENCH_PATHS.root,
      operational: true,
      admin: true,
    },
  ],
  [
    'super_admin',
    {
      role: 'manager',
      modes: ALL_MODES,
      landing: WORKBENCH_PATHS.root,
      operational: true,
      admin: true,
    },
  ],
  [
    'owner',
    {
      role: 'manager',
      modes: ['scorecard', 'operations', 'portfolio', 'schedule', 'my-work'],
      landing: WORKBENCH_PATHS.root,
      operational: true,
      admin: true,
    },
  ],
  // Commercial roles sit with management but hold no engineer authority.
  [
    'account_manager',
    {
      role: 'manager',
      modes: ['portfolio'],
      landing: WORKBENCH_PATHS.portfolioChair,
      operational: false,
      admin: false,
    },
  ],
  [
    'csm',
    {
      role: 'manager',
      modes: ['portfolio'],
      landing: WORKBENCH_PATHS.portfolioChair,
      operational: false,
      admin: false,
    },
  ],
  [
    'finance',
    {
      role: 'manager',
      modes: ['scorecard'],
      landing: WORKBENCH_PATHS.root,
      operational: false,
      admin: false,
    },
  ],
  [
    'billing',
    {
      role: 'manager',
      modes: ['scorecard'],
      landing: WORKBENCH_PATHS.root,
      operational: false,
      admin: false,
    },
  ],
  [
    'readonly',
    {
      role: 'manager',
      modes: ['operations'],
      landing: WORKBENCH_PATHS.root,
      operational: false,
      admin: false,
    },
  ],
  [
    'viewer',
    {
      role: 'manager',
      modes: ['operations'],
      landing: WORKBENCH_PATHS.root,
      operational: false,
      admin: false,
    },
  ],
]);

/** Unknown roles get the narrowest profile: no engineer authority, read chair only. */
const UNKNOWN: Membership = {
  role: 'manager',
  modes: ['operations'],
  landing: WORKBENCH_PATHS.root,
  operational: false,
  admin: false,
};

export interface WorkbenchRoleProfile {
  role: WorkbenchRole;
  label: string;
  /** The concrete role the profile was derived from (lower-cased), or null. */
  membership: string | null;
  modes: readonly WorkbenchMode[];
  defaultMode: WorkbenchMode;
  views: readonly WorkbenchView[];
  landing: string;
  /** May use the phone-first caller lookup (backend engineer guard). */
  canLookupCallers: boolean;
}

interface RoleHolder {
  role?: string | null;
  roles?: readonly string[] | null;
}

const UNIVERSAL_VIEWS: readonly WorkbenchView[] = [
  'overview',
  'queue',
  'communications',
  'tools',
  'activity',
];

function viewsFor(membership: Membership): WorkbenchView[] {
  const views: WorkbenchView[] = [...UNIVERSAL_VIEWS];
  if (membership.admin) views.push('review');
  if (membership.operational) views.push('caller');
  if (membership.operational && (membership.role !== 'manager' || membership.admin)) {
    views.push('calls', 'schedule');
  }
  if (membership.operational && membership.role !== 'engineer') views.push('dispatch');
  return views;
}

/**
 * The user's primary role decides the profile: `role` first, then the first
 * listed membership, matching how the auth API reports the primary role.
 */
export function workbenchRoleProfile(user: RoleHolder | string | null | undefined): WorkbenchRoleProfile {
  const primary =
    typeof user === 'string'
      ? user
      : user?.role?.trim() || user?.roles?.find((role) => role.trim()) || '';
  const membershipName = primary.trim().toLowerCase() || null;
  const membership = (membershipName && MEMBERSHIPS.get(membershipName)) || UNKNOWN;
  return {
    role: membership.role,
    label: WORKBENCH_ROLE_LABELS[membership.role],
    membership: membershipName,
    modes: membership.modes,
    defaultMode: membership.modes[0],
    views: viewsFor(membership),
    landing: membership.landing,
    canLookupCallers: membership.operational,
  };
}
